import React, { Suspense, useState, useEffect, useMemo, useCallback } from 'react';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import { FileDropzone, FileWithPreview, FileUploadTypes, PageSpinner } from '@td-client-frontend/common';
import { ControlsCycle, Copy, Download, Template as TemplateIcon, SelectCheckbox } from '@td-client-frontend/icons';
import { GoPencil } from 'react-icons/go';
import TemplateEntity from '../../components/TemplateEnitity/TemplateEntity';
import Modal from '../../components/Modal/NewModal';
import { Button } from '../../components/Button';
import { TemplateSelect } from '../../components/TemplateSelect';
import { TemaplateSettings, TemplateNameModal, CopyTemplateModal, TemplateModalType } from './components';
import { useDefaults, useDefaultTemplateObject } from '../../hooks/useDefaults';
import { useTemplate, useTemplateCopy } from '../../hooks/useTemplateList';
import { useResellerApi } from '../../hooks/useResellerApi';
import { EntityTypes, ReferenceData, Template, TransformResponse } from '../../@types/Templates';
import { TemplateEntityContextProvider } from '../../context/TemplateEntityContext';

export function TemplateBuilder() {
  // Modals
  const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [showNewTemplateModal, setShowNewTemplateModal] = useState<boolean>(false);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showSelectTemplateModal, setShowSelectTemplateModal] = useState<boolean>(false);

  const toggleUploadModal = () => setShowUploadModal(!showUploadModal);
  const toggleSettingsModal = () => setShowSettingsModal(!showSettingsModal);
  const toggleNewTemplateModal = () => setShowNewTemplateModal(!showNewTemplateModal);
  const toggleRenameModal = () => setShowRenameModal(!showRenameModal);

  // Copy
  const copyTemplate = useTemplateCopy();

  // Template Data
  const { transform, saveTemplate } = useResellerApi();

  // Files BL
  const [zipFile, setZipFile] = useState<FileWithPreview>();
  const [uploadedFile, setUploadedFile] = useState<FileWithPreview>();

  const onZipFileSubmit = () => {
    setZipFile(uploadedFile);
    onModalClose();
  };

  const onZipFileUpload = (files: FileWithPreview[]) => {
    // multiple option is disabled. The only file is available. Select the first one
    setUploadedFile(files[0]);
  };

  const onModalClose = () => {
    setUploadedFile(undefined);
    toggleUploadModal();
  };

  // Templates BL
  const [templateName, setTemplateName] = useState<string>('');

  const [showCopyTemplateModal, setShowCopyTemplateModal] = useState<boolean>(false);
  const toggleCopyTemplateModal = () => setShowCopyTemplateModal(!showCopyTemplateModal);
  const [template, setTemplate] = useState<Template | undefined>();

  const { data: defaultTemplateObject } = useDefaultTemplateObject(setTemplate);

  const onEntityChange = useCallback(
    async (template) => {
      try {
        // @ts-ignore
        const transformResult = await transform({ zipFile, template });
        setTemplate(template);
        setTransformedData(transformResult);
      } catch (error) {
        throw new Error('Error occurred while saving defaults');
      }
    },
    [transform, zipFile]
  );

  // Defaults
  const { defaults, overrides, disabled, onDefaultsChange, setTemplateDefaults } = useDefaults(
    template,
    onEntityChange
  );

  const { data: templateData, isFetching: templateFetching } = useTemplate(templateName);

  const [transformedData, setTransformedData] = useState<TransformResponse>({});

  useEffect(() => {
    if (!templateFetching) {
      setTemplate(templateData);
      if (templateData?.defaults) {
        setTemplateDefaults(templateData.defaults);
      }
    }
  }, [setTemplateDefaults, templateData, templateFetching]);

  useEffect(() => {
    if (zipFile && !templateName) {
      toast.error('Please select template to perform transform', {
        position: 'top-center',
        hideProgressBar: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  }, [zipFile, templateName]);

  useEffect(() => {
    if (!zipFile || !template) {
      return;
    }
    transform({ zipFile, template }).then((data) => {
      setTransformedData(data);
    });
  }, [zipFile, template, transform]);

  const onSaveTemplate = () => {
    saveTemplate({
      templateName: templateName,
      templateJson: template
    })
      .then(() => {
        toast.success(`The "${templateName}.json" was successfully updated`, {
          position: 'top-center'
        });
      })
      .catch(() => {
        toast.error(`Сannot be renamed to ${templateName}!`, { position: 'top-center' });
      });
  };

  const onCreateTemplate = (templateName: string) => {
    saveTemplate({
      templateName: templateName,
      templateJson: defaultTemplateObject
    })
      .then(() => {
        setTemplateName(templateName);
        toast.success(`The "${templateName}.json" was successfully created`, { position: 'top-center' });
      })
      .catch(() => {
        toast.error(`Сannot create ${templateName}!`, { position: 'top-center' });
      });
  };

  const handleTemplateRename = (newTemplateName: string) => {
    copyTemplate(newTemplateName, templateName, true)
      .then(() => {
        toast.success(`Template successfully renamed to ${newTemplateName}!`, { position: 'top-center' });
        setTemplateName(newTemplateName);
      })
      .catch(() => {
        toast.error(`Сannot be renamed to ${newTemplateName}!`, { position: 'top-center' });
      });
  };

  const [selected, setSelected] = useState<string>('');

  const toggleShowTemplateModal = useCallback(() => {
    setShowSelectTemplateModal(!showSelectTemplateModal);
    setSelected('');
  }, [showSelectTemplateModal]);

  const CustomButtons = () => {
    return (
      <div className="flex gap-4">
        <Button title="Cancel" kind="secondary" onClick={toggleShowTemplateModal} />
        <Button
          title="Submit"
          type="submit"
          tooltip="Please select valid Template Name"
          id="tooltip"
          disabled={!selected}
          onClick={() => {
            setTemplateName(selected);
            toggleShowTemplateModal();
          }}
        />
      </div>
    );
  };

  const CustomUploadButtons = () => {
    return (
      <div className="flex gap-4">
        <Button title="Cancel" kind="secondary" onClick={onModalClose} />
        <Button
          title="Submit"
          type="submit"
          tooltip="Please select valid file"
          id="tooltip"
          disabled={!uploadedFile}
          onClick={onZipFileSubmit}
        />
      </div>
    );
  };

  const transformedDataContent = useMemo(() => {
    const referenceDataKeys: string[] = Object.keys(transformedData);
    const referenceData: Omit<ReferenceData, 'entityType'> = referenceDataKeys.reduce(
      (acc: Omit<ReferenceData, 'entityType'>, dataKey: string) => {
        return {
          // @ts-ignore
          fields: { ...acc.fields, [dataKey]: transformedData[dataKey].firstLine },
          // @ts-ignore
          rows: { ...acc.rows, [dataKey]: transformedData[dataKey].firstDataLine }
        };
      },
      { fields: {}, rows: {} }
    );

    return (
      <>
        {referenceDataKeys?.map((dataKey, index) => {
          return (
            <TemplateEntityContextProvider
              key={`${dataKey}-${index}-provider`}
              referenceData={{ ...referenceData, entityType: dataKey }}
            >
              <TemplateEntity
                key={`${dataKey}-${index}`}
                entityType={dataKey as EntityTypes}
                // @ts-ignore
                transformedData={transformedData[dataKey]}
                template={template}
                defaults={defaults?.[dataKey]}
                overrides={overrides}
                disabled={disabled}
                onEntityChange={onEntityChange}
                onDefaultsChange={onDefaultsChange}
                isLastEntity={index === referenceDataKeys.length - 1}
              />
            </TemplateEntityContextProvider>
          );
        })}
      </>
    );
  }, [defaults, overrides, disabled, onDefaultsChange, onEntityChange, template, transformedData]);

  return (
    <div className="px-6 pb-20">
      {/* Header */}
      <div className="flex justify-between items-center w-full my-8">
        <h1 className="text-4xl font-effra font-bold">Reseller Templates</h1>
        <div className="flex gap-6 items-center">
          {templateName && (
            <p className="truncate">
              {`Template: ${templateName}`}
              <button onClick={toggleRenameModal} data-testid="RenameModal">
                <GoPencil size={13} className="ml-1" />
              </button>
            </p>
          )}
          {zipFile && <p className="truncate">{`File: ${zipFile.name}`}</p>}
        </div>
      </div>
      <div className="flex gap-6 flex-col sm:flex-row sm:flex-wrap justify-end py-5 w-full">
        <button
          onClick={toggleShowTemplateModal}
          className="flex gap-1 items-center font-medium text-base text-primary-500"
        >
          <SelectCheckbox className="fill-current text-primary-500" />
          Select Template
        </button>
        <button
          onClick={toggleCopyTemplateModal}
          className="flex gap-1 items-center font-medium text-base text-primary-500"
        >
          <Copy className="fill-current text-primary-500" />
          Copy from Existing Template
        </button>
        <button
          onClick={toggleNewTemplateModal}
          className="flex gap-1 items-center font-medium text-base text-primary-500"
        >
          <TemplateIcon className="fill-current text-primary-500" />
          New Template
        </button>
        <button onClick={toggleUploadModal} className="flex gap-1 items-center font-medium text-base text-primary-500">
          <Download className="transform rotate-180 fill-current text-primary-500" />
          Upload file
        </button>
        {zipFile && templateName && (
          <button
            onClick={toggleSettingsModal}
            className="flex gap-1 items-center font-medium text-base text-primary-500"
          >
            <ControlsCycle className="fill-current text-primary-500" />
            Template Setting
          </button>
        )}
      </div>
      {/* Body */}
      {transformedDataContent}
      {/* Footer */}
      <div
        className={clsx(
          'flex flex-col sm:flex-row sm:flex-wrap justify-end fixed border-t-1 border-gray-20',
          'bottom-0 right-0 px-6 py-5 w-full bg-white z-30 pb-16 transition-all duration-150'
        )}
      >
        <Button
          title="Save"
          id="saveButton"
          disabled={!templateName}
          tooltip="Please select template"
          onClick={onSaveTemplate}
        />
      </div>

      {/* Modals */}

      <Modal
        isOpen={showUploadModal}
        onClose={onModalClose}
        header="Upload file"
        size="small"
        customFooterActions={{
          right: <CustomUploadButtons />
        }}
      >
        <section className="p-6 font-bold w-full">
          <FileDropzone
            onRemove={onZipFileUpload}
            onUpload={onZipFileUpload}
            maxFileSizeMB={25}
            mimeTypes={FileUploadTypes.ZIP}
          />
        </section>
      </Modal>

      <Modal
        isOpen={showSettingsModal}
        onClose={toggleSettingsModal}
        contentLabel="Template settings modal"
        title="Template settings modal"
        header="Template Settings"
        size="default"
      >
        <TemaplateSettings template={template} onSubmit={setTemplate} onClose={toggleSettingsModal} />
      </Modal>

      <CopyTemplateModal
        isOpen={showCopyTemplateModal}
        toggleCopyTemplateModal={toggleCopyTemplateModal}
        copyTemplate={copyTemplate}
        onCopySuccess={setTemplateName}
      />

      <Modal
        isOpen={showSelectTemplateModal}
        onClose={toggleShowTemplateModal}
        header="Select Template"
        size="default"
        customFooterActions={{
          right: <CustomButtons />
        }}
      >
        <TemplateSelect onSelected={setSelected} />
      </Modal>
      <TemplateNameModal
        type={TemplateModalType.CREATE}
        isOpen={showNewTemplateModal}
        onClose={toggleNewTemplateModal}
        onSubmit={onCreateTemplate}
      />
      <TemplateNameModal
        type={TemplateModalType.UPDATE}
        isOpen={showRenameModal}
        onClose={toggleRenameModal}
        onSubmit={handleTemplateRename}
        oldTemplateName={templateName}
      />
    </div>
  );
}

const SuspenseWithLoader = () => {
  return (
    <Suspense fallback={<PageSpinner />}>
      <TemplateBuilder />
    </Suspense>
  );
};

export default SuspenseWithLoader;
